// card-fan
import { onDestroy, onMount } from "@/modules/_";
import gsap, { Draggable, ScrollTrigger, reduced } from "@lib/gsap";
import { Resize } from "@lib/subs";

// 991 matches Webflow's desktop/tablet breakpoint so JS mode flips with any responsive CSS
const DESKTOP_MIN_PX = 992;

const FAN_FILL_RATIO = 0.92;
const MAX_OVERLAP_RATIO = 0.7;
const SCATTER_XY_PCT = 5;
const SCATTER_ROT_DEG = 20;
const FAN_ACTIVE_SCALE = 1.08;
const NEIGHBOR_PUSH_RATIO = 0.3;
const FAN_TWEEN_DURATION = 0.7;
const FAN_TWEEN_EASE = "expo.out";

// x/y are xPercent/yPercent; distance is signed slot offset from active card (wraps)
const DECK_POSE = {
  active: { x: 0, y: 0, rot: 0, s: 1, o: 1, z: 5 },
  near: { x: 25, y: 1, rot: 10, s: 0.9, o: 1, z: 4 },
  far: { x: 45, y: 5, rot: 15, s: 0.8, o: 1, z: 3 },
  hidden: { x: 55, y: 5, rot: 20, s: 0.6, o: 0, z: 2 },
} as const;
const DECK_DRAG_THRESHOLD = 0.1;
const DECK_TWEEN_DURATION = 0.6;
const DECK_TWEEN_EASE = "elastic.out(1.2, 1)";
const DECK_RELEASE_EASE = "power1.out";

// Scroll-in intro — fan: cards arrive as a squared-up pile that pops in, then fans outward
// from the center card; deck: the visible poses rise into place, active card landing last.
const INTRO_TRIGGER_START = "top 80%";
const FAN_INTRO_STACK_ROT_DEG = 8;
const FAN_INTRO_STACK_SCALE = 0.92;
const FAN_INTRO_POP_DURATION = 0.3;
const FAN_INTRO_FAN_DELAY = 0.25;
const FAN_INTRO_DURATION = 0.9;
const FAN_INTRO_STAGGER = 0.07;
const FAN_INTRO_EASE = "back.out(1.2)";
const DECK_INTRO_Y_PCT = 120;
const DECK_INTRO_DURATION = 0.7;
const DECK_INTRO_STAGGER = 0.12;
const DECK_INTRO_EASE = "back.out(1.2)";

const isFinePointer = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const pickMode = (): "fan" | "deck" =>
  window.innerWidth < DESKTOP_MIN_PX ? "deck" : "fan";

export default function (element: HTMLElement) {
  const cards = Array.from(
    element.querySelectorAll<HTMLElement>('[card-fan="card"]'),
  );
  if (cards.length === 0) return;

  const randPct = () => (Math.random() - 0.5) * SCATTER_XY_PCT;
  const randRot = () => (Math.random() - 0.5) * SCATTER_ROT_DEG;

  // Plays once per page view, in whichever mode is mounted when the section scrolls in.
  let introDone = false;

  function mountFan(): () => void {
    let positionsX: number[] = [];
    let centerY = 0;
    let cardW = 0;

    const defaultZ = (i: number) =>
      cards.length - Math.abs(i - (cards.length - 1) / 2);

    function computeLayout(): boolean {
      const viewportW = window.innerWidth;
      const sectionRect = element.getBoundingClientRect();
      const sectionH = element.clientHeight;
      if (viewportW <= 0 || sectionH <= 0) return false;

      cardW = cards[0]!.offsetWidth;
      const cardH = cards[0]!.offsetHeight;
      if (cardW <= 0 || cardH <= 0) return false;

      const minStep = cardW * (1 - MAX_OVERLAP_RATIO);
      const maxStep = cardW;
      const targetFanW = viewportW * FAN_FILL_RATIO;
      const idealStep =
        cards.length > 1 ? (targetFanW - cardW) / (cards.length - 1) : 0;
      const step = Math.min(maxStep, Math.max(minStep, idealStep));
      const totalW = cardW + step * (cards.length - 1);

      const viewportCenterX = viewportW / 2;
      const sectionLeft = sectionRect.left;
      const startX = viewportCenterX - totalW / 2 - sectionLeft;
      centerY = (sectionH - cardH) / 2;

      positionsX = cards.map((_, i) => startX + i * step);
      return true;
    }

    function applyLayout() {
      cards.forEach((card, i) => {
        gsap.set(card, {
          position: "absolute",
          top: 0,
          left: 0,
          x: positionsX[i],
          y: centerY,
          zIndex: defaultZ(i),
          transformOrigin: "50% 50%",
        });
      });
    }

    function applyInitialScatter() {
      cards.forEach((card) => {
        gsap.set(card, {
          xPercent: randPct(),
          yPercent: randPct(),
          rotation: randRot(),
          scale: 1,
          opacity: 1,
        });
      });
    }

    function applyActive(i: number, lastActive: number) {
      cards.forEach((card, index) => {
        if (index === i) {
          gsap.set(card, { zIndex: cards.length + 1 });
          gsap.to(card, {
            x: positionsX[i],
            y: centerY,
            xPercent: 0,
            yPercent: 0,
            rotation: 0,
            scale: FAN_ACTIVE_SCALE,
            duration: FAN_TWEEN_DURATION,
            ease: FAN_TWEEN_EASE,
          });
          return;
        }
        const dist = index - i;
        const dx = (cardW * NEIGHBOR_PUSH_RATIO) / dist;
        if (index === lastActive) {
          gsap.set(card, { zIndex: defaultZ(index) });
          gsap.to(card, {
            x: positionsX[index] + dx,
            y: centerY,
            xPercent: randPct(),
            yPercent: randPct(),
            rotation: randRot(),
            scale: 1,
            duration: FAN_TWEEN_DURATION,
            ease: FAN_TWEEN_EASE,
          });
          return;
        }
        gsap.to(card, {
          x: positionsX[index] + dx,
          duration: FAN_TWEEN_DURATION,
          ease: FAN_TWEEN_EASE,
        });
      });
    }

    function resetAll(lastActive: number) {
      cards.forEach((card, index) => {
        const vars: gsap.TweenVars = {
          x: positionsX[index],
          duration: FAN_TWEEN_DURATION,
          ease: FAN_TWEEN_EASE,
        };
        if (index === lastActive) {
          vars.y = centerY;
          vars.xPercent = randPct();
          vars.yPercent = randPct();
          vars.rotation = randRot();
          vars.scale = 1;
        }
        gsap.set(card, { zIndex: defaultZ(index) });
        gsap.to(card, vars);
      });
    }

    if (!computeLayout()) return () => {};
    applyLayout();

    if (reduced) {
      cards.forEach((c) =>
        gsap.set(c, { xPercent: 0, yPercent: 0, rotation: 0, scale: 1 }),
      );
      const unsubResize = Resize.add(() => {
        if (computeLayout()) applyLayout();
      });
      return () => unsubResize();
    }

    let activeIndex = -1;

    const onMove = (e: MouseEvent) => {
      if (cards.length === 0 || positionsX.length === 0) return;
      const totalW =
        cards.length > 1
          ? positionsX[cards.length - 1]! - positionsX[0]! + cardW
          : cardW;
      const sectionLeft = element.getBoundingClientRect().left;
      const fanLeftViewport = sectionLeft + positionsX[0]!;
      const xInFan = e.clientX - fanLeftViewport;
      if (xInFan < 0 || xInFan > totalW) return;
      const portion = Math.ceil((xInFan / totalW) * cards.length);
      const nextIndex = portion - 1;
      if (nextIndex < 0 || nextIndex >= cards.length) return;
      if (nextIndex === activeIndex) return;
      applyActive(nextIndex, activeIndex);
      activeIndex = nextIndex;
    };

    const onLeave = () => {
      resetAll(activeIndex);
      activeIndex = -1;
    };

    // Hover spread stays disarmed until the intro lands — a mousemove mid-intro would
    // start applyActive tweens that fight the intro for x/rotation.
    let armed = false;
    const armInteractions = () => {
      if (armed) return;
      armed = true;
      if (!isFinePointer()) return;
      element.addEventListener("mousemove", onMove);
      element.addEventListener("mouseleave", onLeave);
    };

    let introTl: gsap.core.Timeline | null = null;
    let introTrigger: ScrollTrigger | null = null;

    function killIntro() {
      introTrigger?.kill();
      introTrigger = null;
      introTl?.kill();
      introTl = null;
    }

    // The pile pose is applied at play time (cards are merely hidden until the trigger
    // fires), so the resting fan is never visible pre-trigger.
    function buildIntro() {
      gsap.set(cards, { opacity: 0 });
      const stackX = (positionsX[0]! + positionsX[cards.length - 1]!) / 2;
      const mid = (cards.length - 1) / 2;
      introTl = gsap.timeline({
        paused: true,
        onStart: () => {
          introDone = true;
        },
        onComplete: () => {
          introTl = null;
          introTrigger = null;
          armInteractions();
        },
      });
      cards.forEach((card) => {
        introTl!.set(
          card,
          {
            x: stackX,
            y: centerY,
            xPercent: 0,
            yPercent: 0,
            rotation: (Math.random() - 0.5) * FAN_INTRO_STACK_ROT_DEG,
            scale: FAN_INTRO_STACK_SCALE,
            opacity: 0,
          },
          0,
        );
      });
      introTl.to(
        cards,
        {
          opacity: 1,
          scale: 1,
          duration: FAN_INTRO_POP_DURATION,
          ease: "power2.out",
        },
        0,
      );
      cards.forEach((card, i) => {
        introTl!.to(
          card,
          {
            x: positionsX[i],
            xPercent: randPct(),
            yPercent: randPct(),
            rotation: randRot(),
            duration: FAN_INTRO_DURATION,
            ease: FAN_INTRO_EASE,
          },
          FAN_INTRO_FAN_DELAY + Math.abs(i - mid) * FAN_INTRO_STAGGER,
        );
      });
      introTrigger = ScrollTrigger.create({
        trigger: element,
        start: INTRO_TRIGGER_START,
        once: true,
        animation: introTl,
      });
    }

    if (!introDone) {
      buildIntro();
    } else {
      applyInitialScatter();
      armInteractions();
    }

    const unsubResize = Resize.add(() => {
      if (!computeLayout()) return;
      if (introTl && !introDone) {
        // Layout moved before the intro ever played — rebuild it against the new positions.
        killIntro();
        buildIntro();
        return;
      }
      if (introTl) {
        // Mid-flight: the tweens' captured positions are stale — snap to the resting pose.
        killIntro();
        gsap.killTweensOf(cards);
        applyInitialScatter();
        armInteractions();
      }
      cards.forEach((card, i) =>
        gsap.set(card, { x: positionsX[i], y: centerY }),
      );
    });

    return () => {
      killIntro();
      element.removeEventListener("mousemove", onMove);
      element.removeEventListener("mouseleave", onLeave);
      unsubResize();
      gsap.killTweensOf(cards);
    };
  }

  function mountDeck(): () => void {
    const total = cards.length;
    let activeIndex = 0;
    let cardW = 0;

    function positionStack(): boolean {
      const viewportW = window.innerWidth;
      const sectionRect = element.getBoundingClientRect();
      const sectionH = element.clientHeight;
      if (viewportW <= 0 || sectionH <= 0) return false;

      cardW = cards[0]!.offsetWidth;
      const cardH = cards[0]!.offsetHeight;
      if (cardW <= 0 || cardH <= 0) return false;

      // Center each card on the viewport in section-local coords.
      const centerX = viewportW / 2 - cardW / 2 - sectionRect.left;
      const centerY = (sectionH - cardH) / 2;

      cards.forEach((card) => {
        gsap.set(card, {
          position: "absolute",
          top: 0,
          left: 0,
          x: centerX,
          y: centerY,
          transformOrigin: "50% 50%",
        });
      });
      return true;
    }

    function getConfig(i: number, currentIndex: number) {
      let diff = i - currentIndex;
      if (diff > total / 2) diff -= total;
      else if (diff < -total / 2) diff += total;
      const dir = diff > 0 ? 1 : -1;
      switch (diff) {
        case 0:
          return DECK_POSE.active;
        case 1:
          return DECK_POSE.near;
        case -1:
          return {
            ...DECK_POSE.near,
            x: -DECK_POSE.near.x,
            rot: -DECK_POSE.near.rot,
          };
        case 2:
          return DECK_POSE.far;
        case -2:
          return {
            ...DECK_POSE.far,
            x: -DECK_POSE.far.x,
            rot: -DECK_POSE.far.rot,
          };
        default:
          return {
            ...DECK_POSE.hidden,
            x: DECK_POSE.hidden.x * dir,
            rot: DECK_POSE.hidden.rot * dir,
          };
      }
    }

    function renderCards(currentIndex: number, animate = true) {
      cards.forEach((card, i) => {
        const cfg = getConfig(i, currentIndex);
        gsap.set(card, { zIndex: cfg.z });
        const target = {
          xPercent: cfg.x,
          yPercent: cfg.y,
          rotation: cfg.rot,
          scale: cfg.s,
          opacity: cfg.o,
        };
        if (animate && !reduced) {
          gsap.to(card, {
            ...target,
            duration: DECK_TWEEN_DURATION,
            ease: DECK_TWEEN_EASE,
          });
        } else {
          gsap.set(card, target);
        }
      });
    }

    if (!positionStack()) return () => {};

    let introTl: gsap.core.Timeline | null = null;
    let introTrigger: ScrollTrigger | null = null;
    // Assigned once the Draggable instances exist below.
    let armDrag: () => void = () => {};

    function killIntro() {
      introTrigger?.kill();
      introTrigger = null;
      introTl?.kill();
      introTl = null;
    }

    // Visible poses rise into place far → near → active, so the deck stacks up under the
    // top card. Poses are percent-based, so a resize only moves the base x/y under them.
    function buildIntro() {
      gsap.set(cards, { opacity: 0 });
      introTl = gsap.timeline({
        paused: true,
        onStart: () => {
          introDone = true;
        },
        onComplete: () => {
          introTl = null;
          introTrigger = null;
          armDrag();
        },
      });
      cards.forEach((card, i) => {
        const cfg = getConfig(i, activeIndex);
        gsap.set(card, { zIndex: cfg.z });
        let diff = i - activeIndex;
        if (diff > total / 2) diff -= total;
        else if (diff < -total / 2) diff += total;
        const dist = Math.abs(diff);
        if (dist > 2) {
          // Ends invisible anyway — place directly.
          introTl!.set(
            card,
            {
              xPercent: cfg.x,
              yPercent: cfg.y,
              rotation: cfg.rot,
              scale: cfg.s,
              opacity: 0,
            },
            0,
          );
          return;
        }
        introTl!.set(
          card,
          {
            xPercent: cfg.x,
            yPercent: cfg.y + DECK_INTRO_Y_PCT,
            rotation: cfg.rot,
            scale: cfg.s,
            opacity: 0,
          },
          0,
        );
        introTl!.to(
          card,
          {
            yPercent: cfg.y,
            opacity: cfg.o,
            duration: DECK_INTRO_DURATION,
            ease: DECK_INTRO_EASE,
          },
          (2 - dist) * DECK_INTRO_STAGGER,
        );
      });
      introTrigger = ScrollTrigger.create({
        trigger: element,
        start: INTRO_TRIGGER_START,
        once: true,
        animation: introTl,
      });
    }

    if (!introDone && !reduced) {
      buildIntro();
    } else {
      renderCards(activeIndex, false);
    }

    // Overlay per card acts as gesture sensor only; keeping card transforms under our control.
    const draggers: HTMLElement[] = cards.map((card) => {
      const dragger = document.createElement("div");
      dragger.setAttribute("card-fan-dragger", "");
      Object.assign(dragger.style, {
        position: "absolute",
        inset: "0",
        zIndex: "999",
        touchAction: "pan-y",
        cursor: "grab",
      });
      card.appendChild(dragger);
      return dragger;
    });

    const sliderWidth = element.offsetWidth || window.innerWidth;
    const dragInstances = Draggable.create(draggers, {
      type: "x",
      edgeResistance: 0.8,
      bounds: { minX: -sliderWidth / 2, maxX: sliderWidth / 2 },
      inertia: false,
      onPress(this: any) {
        this.target.style.cursor = "grabbing";
      },
      onDrag(this: any) {
        if (reduced) return;
        const raw = this.x / sliderWidth;
        const progress = Math.min(1, Math.abs(raw));
        const direction = raw > 0 ? -1 : 1;
        const nextIndex = (activeIndex + direction + total) % total;
        cards.forEach((card, i) => {
          const from = getConfig(i, activeIndex);
          const to = getConfig(i, nextIndex);
          const mix = (key: "x" | "y" | "rot" | "s" | "o") =>
            from[key] + (to[key] - from[key]) * progress;
          gsap.set(card, {
            xPercent: mix("x"),
            yPercent: mix("y"),
            rotation: mix("rot"),
            scale: mix("s"),
            opacity: mix("o"),
          });
        });
      },
      onRelease(this: any) {
        this.target.style.cursor = "grab";
        const raw = this.x / sliderWidth;
        let shift = 0;
        if (raw > DECK_DRAG_THRESHOLD) shift = -1;
        else if (raw < -DECK_DRAG_THRESHOLD) shift = 1;
        if (shift !== 0) {
          activeIndex = (activeIndex + shift + total) % total;
        }
        renderCards(activeIndex);
        gsap.to(this.target, {
          x: 0,
          duration: 0.3,
          ease: DECK_RELEASE_EASE,
        });
      },
    });

    if (introTl) dragInstances.forEach((d) => d.disable());
    armDrag = () => dragInstances.forEach((d) => d.enable());

    const unsubResize = Resize.add(() => {
      if (!positionStack()) return;
      // Pending intro survives a resize untouched — its poses are percent-based.
      if (introTl && !introDone) return;
      if (introTl) {
        // Mid-flight: snap to resting poses and hand over to the drag.
        killIntro();
        gsap.killTweensOf(cards);
        armDrag();
      }
      renderCards(activeIndex, false);
    });

    return () => {
      killIntro();
      unsubResize();
      dragInstances.forEach((d) => d.kill());
      draggers.forEach((d) => d.remove());
      gsap.killTweensOf(cards);
    };
  }

  onMount(() => {
    gsap.set(element, { position: "relative" });

    let cleanup: (() => void) | null = null;
    let currentMode: "fan" | "deck" | null = null;

    function switchTo(mode: "fan" | "deck") {
      if (mode === currentMode) return;
      cleanup?.();
      // Reset transforms before switching so the new mode starts clean.
      gsap.set(cards, { clearProps: "transform,opacity,zIndex" });
      cleanup = mode === "deck" ? mountDeck() : mountFan();
      currentMode = mode;
    }

    switchTo(pickMode());

    const unsubResize = Resize.add(() => {
      const next = pickMode();
      if (next !== currentMode) switchTo(next);
      // Same-mode resize is handled by each mount's own listener.
    });

    onDestroy(() => {
      unsubResize();
      cleanup?.();
      gsap.set([element, ...cards], { clearProps: "all" });
    });
  });
}
